import { createStore } from '@stencil/store';
import { Shop, ShopAdvancedOptions, ShopDeveloperOptions, ShopLayout, ShopTheme, ShopType } from '../models/shop.model';
import { getLanguageId, language, LanguageCode } from './utils';
import { ShopSectionType } from '../models/shop-section.model';

export const globalStore = createStore({
  lang: null,
});

export const aboStore = createStore({
  abo: null,
  count: null,
});

export interface ShopIntegrationState {
  isEmbedded: boolean,
  isProfile: boolean
}
export const shopIntegrationState = createStore<ShopIntegrationState>({
  isEmbedded: false,
  isProfile: false
}).state;

export interface ThemeState {
  color: string,
  theme: ShopTheme,
}
export const themeState = createStore<ThemeState>({
  color: null,
  theme: null,
}).state;

export interface ShopConfigState {
  code: string;
  type: ShopType;
  layout: ShopLayout;
  advancedOptions: ShopAdvancedOptions;
  developerOptions: ShopDeveloperOptions;
}
export const shopConfig = createStore<ShopConfigState>({
  code: null,
  type: null,
  layout: {
    backgroundUrl: '',
    posterUrl: '',
    headerType: 'none',
    language: null,
    languages: [],
    sections: [],
    color: '',
    theme: ShopTheme.LIGHT
  },
  advancedOptions: {
    isPortalLogoHidden: false,
    isOnlyPurchaseTunnel: false
  },
  developerOptions: {
    csps: [],
    customCss: null,
    customHtml: null
  }
});

export function getShopLayout(data: Shop): ShopLayout {
  const currentLanguage = data.language ?? data.languages.find((lang) => lang.language_id === getLanguageId(language() as LanguageCode));
  return {
    backgroundUrl: data.background_url,
    posterUrl: data.poster_url,
    headerType: data.header,
    language: currentLanguage,
    languages: data.languages,
    color: data.color,
    theme: data.theme,
    sections: data.sections
  } as ShopLayout;
}

export function getShopAdvancedOptions(data: Shop): ShopAdvancedOptions {
  return {
    isPortalLogoHidden: data.is_portal_logo_hidden,
    isOnlyPurchaseTunnel: data.is_only_purchase_tunnel
  } as ShopAdvancedOptions;
}

export function getShopDeveloperOptions(data: Shop, isPreview = false): ShopDeveloperOptions {
  return {
    customHtml: isPreview ? '' : data.custom_html,
    customCss: data.custom_css,
    csps: isPreview ? [] : data.csps
  } as ShopDeveloperOptions;
}

export const shopConfigState = shopConfig.state;

export default globalStore

export enum DataStatus {
  LOADING = 'loading',
  VALID = 'valid',
  EMPTY = 'empty',
  ERROR = 'error',
}

interface NamedShopSection {
  type: ShopSectionType;
  name: string;
}

interface HomeState {
  sections: NamedShopSection[];
  status: {
    events: DataStatus,
    passes: DataStatus,
    donations: DataStatus,
    vouchers: DataStatus,
    products: DataStatus
  }
}

export const homeStatusInit = {
  events: DataStatus.LOADING,
  passes: DataStatus.LOADING,
  donations: DataStatus.LOADING,
  vouchers: DataStatus.LOADING,
  products: DataStatus.LOADING
}

export const homeSectionsInit = [
  {
    type: ShopSectionType.EVENT,
    name: '',
  },
  {
    type: ShopSectionType.PROTOPASS,
    name: '',
  },
  {
    type: ShopSectionType.DONATION,
    name: '',
  },
  {
    type: ShopSectionType.VOUCHER,
    name: '',
  },
  {
    type: ShopSectionType.METAPRODUCT,
    name: '',
  },
];

export const homeStore = createStore<HomeState>({
  sections: homeSectionsInit,
  status: homeStatusInit
});

export const homeState = homeStore.state;

export const shopSectionStatusMapping = {
  [ShopSectionType.EVENT]: 'events',
  [ShopSectionType.PROTOPASS]: 'passes',
  [ShopSectionType.DONATION]: 'donations',
  [ShopSectionType.VOUCHER]: 'vouchers',
  [ShopSectionType.METAPRODUCT]: 'products',
}

export function setHomeStatus(section: ShopSectionType, status: DataStatus): void {
  homeState.status = {
    ...homeState.status,
    [shopSectionStatusMapping[section]]: status
  }
}

export function getHomeSection(type: ShopSectionType): NamedShopSection {
  return homeState.sections.find((section) => section.type === type);
}

export function isOnlyPurchaseTunnelActive(): boolean {
  return shopIntegrationState.isEmbedded && shopConfigState.advancedOptions.isOnlyPurchaseTunnel
}

export function isDefaultShop(): boolean {
  return shopConfigState.type === ShopType.DEFAULT;
}

export interface CustomPortalState {
  id: number
}

export const CUSTOM_PORTALS = {
  ETICKETS: 234,
  MIGROS: 902531
}

export const customPortalStore = createStore<CustomPortalState>({
  id: CUSTOM_PORTALS.ETICKETS,
});

export function customPortalExists(customPortalId: number): boolean {
  return Object.values(CUSTOM_PORTALS).includes(customPortalId);
}

export const customPortalState = customPortalStore.state;

export function isPortalMigros(): boolean {
  return customPortalState.id === CUSTOM_PORTALS.MIGROS;
}
